import { EMPTY_STRING, EBooleanStatus } from 'commons-ui';
import { BOOLEAN_VALUES } from 'commons-ui/src/constants';
import { ICsvRow, IParseCsvResult, IInvalidCsvRow } from '../types';
import { CSV_ROW_LIMIT, CSV_HEADERS, CSV_ACTIONS, MESSAGES } from '../constants';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (email: string): boolean => EMAIL_REGEX.test(email);
const isValidPhone = (phone: string): boolean => /^\d{10}$/.test(phone);

const normalise = (str: string): string => str.replace(/\s|_/g, '').toLowerCase();

export const parseCsv = (file: File): Promise<IParseCsvResult> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = (e) => {
			try {
				const text = (e.target?.result as string) ?? EMPTY_STRING;
				const lines = text.split('\n').filter((line) => line.trim());

				if (lines.length === 0) {
					throw new Error(MESSAGES.VALIDATION.CSV_EMPTY);
				}

				const rawHeaderCells = lines[0].split(',').map((h) => h.trim());
				const headerCells = rawHeaderCells.map(normalise);

				const expectedHeaders = [
					normalise(CSV_HEADERS.EMAIL),
					normalise(CSV_HEADERS.ACTION),
					normalise(CSV_HEADERS.USERNAME),
					normalise(CSV_HEADERS.PHONE),
					normalise(CSV_HEADERS.DEPARTMENT),
					normalise(CSV_HEADERS.MANAGER),
					normalise(CSV_HEADERS.DESIGNATION),
					normalise(CSV_HEADERS.TEAM),
					normalise(CSV_HEADERS.ROLE_NAME),
					normalise(CSV_HEADERS.PERMISSIONS),
					normalise(CSV_HEADERS.PERMISSION_SET),
					normalise(CSV_HEADERS.EXPIRE_AFTER_SEC),
					normalise(CSV_HEADERS.IS_MASKED)
				];

				const isHeaderValid: boolean =
					headerCells.length === expectedHeaders.length && expectedHeaders.every((h, idx) => headerCells[idx] === h);

				if (!isHeaderValid) {
					throw new Error(MESSAGES.VALIDATION.CSV_INVALID_HEADERS);
				}

				const rows: ICsvRow[] = [];
				const invalidRows: IInvalidCsvRow[] = [];
				for (let i = 1; i < lines.length; i += 1) {
					const values = lines[i].split(',').map((v) => v.trim());

					const [
						email = EMPTY_STRING,
						action = EMPTY_STRING,
						username = EMPTY_STRING,
						phone = EMPTY_STRING,
						department = EMPTY_STRING,
						manager = EMPTY_STRING,
						designation = EMPTY_STRING,
						team = EMPTY_STRING,
						roleName = EMPTY_STRING,
						permissions = EMPTY_STRING,
						permissionSet = EMPTY_STRING,
						expireAfterSec = EMPTY_STRING,
						isMaskedRaw = EMPTY_STRING
					] = values;

					const emailValid = isValidEmail(email);
					const phoneValid = phone === EMPTY_STRING || isValidPhone(phone);

					const actionLower = action.toLowerCase();

					const isCreate = actionLower === CSV_ACTIONS.CREATE.toLowerCase();
					const isDelete = actionLower === CSV_ACTIONS.DELETE.toLowerCase();

					const hasRequiredCreateFields =
						username && department && manager && designation && team && (permissions || permissionSet);

					if (email && emailValid && phoneValid && ((isCreate && hasRequiredCreateFields) || isDelete)) {
						const rowObj: Partial<ICsvRow> = {
							email,
							action: (isCreate ? CSV_ACTIONS.CREATE : CSV_ACTIONS.DELETE) as keyof typeof CSV_ACTIONS
						};

						if (username) rowObj.username = username;
						if (phone) rowObj.phone = phone;
						if (department) rowObj.department = department;
						if (manager) rowObj.manager = manager;
						if (designation) rowObj.designation = designation;
						if (team) rowObj.team = team;
						if (roleName) rowObj.roleName = roleName;
						if (permissions) {
							rowObj.permissions = permissions
								.split('|')
								.map((p) => p.trim())
								.filter(Boolean);
						}
						if (permissionSet) rowObj.permissionSet = permissionSet;
						if (expireAfterSec) rowObj.expireAfter = parseInt(expireAfterSec, 10);
						if (isMaskedRaw) rowObj.isMasked = isMaskedRaw.toLowerCase() === BOOLEAN_VALUES.TRUE;

						rows.push(rowObj as ICsvRow);
					} else {
						let reason: string = MESSAGES.INVALID_ROW_REASONS.MISSING_REQUIRED_FIELDS;
						if (!emailValid) {
							reason = MESSAGES.INVALID_ROW_REASONS.INVALID_EMAIL;
						} else if (!phoneValid) {
							reason = MESSAGES.INVALID_ROW_REASONS.INVALID_PHONE;
						} else if (!isCreate && !isDelete) {
							reason = MESSAGES.INVALID_ROW_REASONS.INVALID_ACTION;
						}
						invalidRows.push({ index: i + 1, reason });
					}
				}

				if (rows.length === 0) {
					throw new Error(MESSAGES.VALIDATION.CSV_NO_DATA);
				}

				if (rows.length > CSV_ROW_LIMIT) {
					throw new Error(MESSAGES.VALIDATION.CSV_ROW_LIMIT_EXCEEDED);
				}

				resolve({ validRows: rows, invalidRows });
			} catch (err) {
				reject(err instanceof Error ? err : new Error(String(err)));
			}
		};

		reader.onerror = () => reject(new Error(MESSAGES.VALIDATION.FILE_READ_ERROR));
		reader.readAsText(file);
	});

export const formatIsMasked = (value?: boolean): string => {
	if (value === undefined) return '-';
	return value ? EBooleanStatus.YES : EBooleanStatus.NO;
};
